import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { servicesData } from '@/data/servicesData';

const CategoryCard = ({ category, itemVariants }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = category.icon;

  // Flatten subcategories count
  const allSubitems = category.hasSubgroups
    ? category.groups.flatMap((g) => g.items)
    : category.items;

  const totalCount = allSubitems.length;

  return (
    <motion.div
      variants={itemVariants}
      whileHover={{ y: -6, transition: { duration: 0.25, ease: 'easeOut' } }}
      className="glass-panel-white p-6 group flex flex-col justify-between transition-all duration-300 hover:border-[#F3453F]/50 hover:shadow-xl relative overflow-hidden"
    >
      <div>
        {/* Top Header: Icon & Category Name */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#F3453F]/10 text-[#F3453F] flex items-center justify-center p-2.5 flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
              <Icon size={22} />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-[#181F32] font-heading group-hover:text-[#F3453F] transition-colors leading-snug">
                {category.title}
              </h3>
              <span className="text-[11px] font-bold text-[#F3453F] font-sans">
                {totalCount} Specialized Services
              </span>
            </div>
          </div>
        </div>

        {/* Short Description */}
        <p className="text-[#181F32]/80 text-xs sm:text-sm leading-relaxed font-sans mb-4 font-medium">
          {category.description}
        </p>

        {/* Subcategories Container */}
        <div className="pt-3 border-t border-gray-200/80">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-extrabold text-[#181F32]/70 uppercase tracking-wider block">
              Subcategories ({totalCount})
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="inline-flex items-center gap-1 text-[11px] font-extrabold text-[#F3453F] hover:text-[#D93833] focus:outline-none transition-colors"
              aria-label={`Toggle ${category.title} subcategories`}
            >
              <span>{isExpanded ? 'Show Less' : 'Expand All'}</span>
              <ChevronDown
                size={14}
                className={`transform transition-transform duration-300 ${
                  isExpanded ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
          </div>

          {/* Subcategories list / grid */}
          <div className="space-y-2">
            {category.hasSubgroups ? (
              category.groups.map((group) => {
                const visibleItems = isExpanded ? group.items : group.items.slice(0, 3);
                return (
                  <div key={group.name} className="space-y-1">
                    <span className="text-[10px] font-extrabold text-[#F3453F] uppercase tracking-wider block">
                      {group.name}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {visibleItems.map((subItem) => (
                        <button
                          key={subItem.name}
                          onClick={() => {
                            navigate(subItem.path);
                            window.scrollTo(0, 0);
                          }}
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md bg-gray-100 text-gray-800 hover:bg-[#F3453F] hover:text-white transition-all transform hover:-translate-y-0.5"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#F3453F]" />
                          {subItem.name}
                        </button>
                      ))}
                      {!isExpanded && group.items.length > 3 && (
                        <button
                          onClick={() => setIsExpanded(true)}
                          className="text-[11px] font-bold text-[#F3453F] hover:underline self-center px-1"
                        >
                          +{group.items.length - 3} more
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(isExpanded ? category.items : category.items.slice(0, 4)).map((subItem) => (
                  <button
                    key={subItem.name}
                    onClick={() => {
                      navigate(subItem.path);
                      window.scrollTo(0, 0);
                    }}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-md bg-gray-100 text-gray-800 hover:bg-[#F3453F] hover:text-white transition-all transform hover:-translate-y-0.5"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F3453F]" />
                    {subItem.name}
                  </button>
                ))}
                {!isExpanded && category.items.length > 4 && (
                  <button
                    onClick={() => setIsExpanded(true)}
                    className="text-xs font-bold text-[#F3453F] hover:underline self-center px-1"
                  >
                    +{category.items.length - 4} more
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Know More Button - Redirects directly to category page */}
      <button
        onClick={() => {
          navigate(category.categoryPath);
          window.scrollTo(0, 0);
        }}
        className="w-full mt-5 pt-3 border-t border-gray-200/80 flex items-center justify-between font-extrabold text-[#F3453F] hover:text-[#D93833] cursor-pointer transition-colors text-sm group/km"
      >
        <span>Know More</span>
        <div className="w-8 h-8 rounded-full bg-[#F3453F]/10 group-hover/km:bg-[#F3453F] group-hover/km:text-white flex items-center justify-center transition-all duration-300">
          <ArrowRight size={16} />
        </div>
      </button>
    </motion.div>
  );
};

const ServicesSection = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 35, scale: 0.97 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, ease: [0.215, 0.61, 0.355, 1] }
    }
  };

  return (
    <section 
      id="services"
      className="py-16 sm:py-20 lg:py-24 bg-[#181F32] relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
          className="text-center mb-12 sm:mb-16"
        >
          <span className="text-[#F3453F] font-bold text-sm tracking-wider uppercase font-sans mb-2 block">
            End-To-End Growth Solutions
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-white font-heading">
            Our Digital Marketing <span className="text-[#F3453F]">Expertise</span>
          </h2>
          <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto font-sans leading-relaxed">
            Comprehensive marketing strategy, technology automation, and brand creative tailored to drive qualified leads and revenue growth.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {servicesData.map((category) => (
            <CategoryCard key={category.id} category={category} itemVariants={itemVariants} />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-20px' }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-14 text-center"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              navigate('/services');
              window.scrollTo(0, 0);
            }}
            className="btn-glass btn-glass-primary px-8 py-4 text-base"
          >
            Explore All Detailed Services
            <ArrowRight size={20} className="ml-2 inline-block" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;

